import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import api from '../../utils/api';
import EmployeeSidebar from '../../components/employee/EmployeeSidebar';
import { useAuth } from '../../hooks/useAuth';
import ImportTenantsModal from '../../components/ImportTenantsModal';
import AddLandlordModal from '../../components/employee/AddLandlordModal';
import AddPropertyModal from '../../components/employee/AddPropertyModal';
import LegalFooter from '../../components/shared/LegalFooter';

const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';

export default function EmployeeLandlords() {
  const { role } = useAuth();
  const navigate = useNavigate();
  const [landlords, setLandlords] = useState([]);
  const [accountManagers, setAccountManagers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [assigning, setAssigning] = useState({});
  const [expanded, setExpanded] = useState({});
  const [properties, setProperties] = useState({});
  const [showImport, setShowImport] = useState(false);
  const [showAddLandlord, setShowAddLandlord] = useState(false);
  const [showAddProperty, setShowAddProperty] = useState(false);

  const isSuperAdmin = role === 'super_admin';

  useEffect(() => {
    Promise.all([
      api.get('/employee/landlords'),
      isSuperAdmin ? api.get('/employee/staff') : Promise.resolve({ data: { staff: [] } }),
    ]).then(([llRes, staffRes]) => {
      setLandlords(llRes.data.landlords);
      setAccountManagers(staffRes.data.staff.filter(s => s.role === 'account_manager' && s.status === 'active'));
    }).catch(() => toast.error('Could not load landlords'))
      .finally(() => setLoading(false));
  }, [isSuperAdmin]);

  const assignManager = async (landlord_id, employee_id) => {
    setAssigning(prev => ({ ...prev, [landlord_id]: true }));
    try {
      await api.post(`/employee/landlords/${landlord_id}/assign-manager`, { employee_id: employee_id || null });
      toast.success('Account manager assigned!');
      const res = await api.get('/employee/landlords');
      setLandlords(res.data.landlords);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not assign manager');
    } finally {
      setAssigning(prev => ({ ...prev, [landlord_id]: false }));
    }
  };

  const reloadLandlords = () => {
    api.get('/employee/landlords').then(res => setLandlords(res.data.landlords)).catch(() => {});
  };

  const toggleLandlord = async (landlordId) => {
    if (expanded[landlordId]) {
      setExpanded(prev => ({ ...prev, [landlordId]: false }));
      return;
    }
    try {
      const res = await api.get(`/employee/properties?landlord_id=${landlordId}`);
      setProperties(prev => ({ ...prev, [landlordId]: res.data.properties }));
      setExpanded(prev => ({ ...prev, [landlordId]: true }));
    } catch (err) {
      toast.error('Could not load properties');
    }
  };

  const filtered = landlords.filter(l =>
    search === '' ||
    `${l.first_name} ${l.last_name} ${l.company_name} ${l.email}`.toLowerCase().includes(search.toLowerCase())
  );


  const exportCSV = () => {
    const headers = ['First Name', 'Last Name', 'Company', 'Email', 'Phone', 'Properties', 'Active Tenants', 'Monthly Revenue', 'Status', 'Since'];
    const rows = filtered.map(l => [
      l.first_name, l.last_name, l.company_name || '', l.email, l.phone || '',
      l.property_count, l.tenant_count,
      l.monthly_revenue ? parseFloat(l.monthly_revenue).toFixed(2) : '0.00',
      l.status, l.created_at ? new Date(l.created_at).toLocaleDateString() : ''
    ]);
    const csv = [headers, ...rows].map(r => r.map(v => '"' + String(v).replace(/"/g, '""') + '"').join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'landlords_export_' + new Date().toISOString().split('T')[0] + '.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const exportPropertiesCSV = async () => {
    try {
      const res = await api.get('/employee/properties');
      const props = res.data.properties || [];
      const headers = ['Name', 'Address', 'City', 'State', 'ISP', 'Total Units', 'Active Tenants', 'Penetration %', 'Monthly Revenue', 'Go-Live Date', 'Ramp-Up Date', 'Status'];
      const rows = props.map(p => [
        p.name, p.address, p.city, p.state, p.isp || '',
        p.total_units, p.active_tenants,
        p.total_units > 0 ? Math.round((p.active_tenants / p.total_units) * 100) + '%' : '0%',
        p.monthly_revenue ? parseFloat(p.monthly_revenue).toFixed(2) : '0.00',
        p.go_live_date ? new Date(p.go_live_date).toLocaleDateString() : '',
        p.ramp_up_date ? new Date(p.ramp_up_date).toLocaleDateString() : '',
        p.status,
      ]);
      const csv = [headers, ...rows].map(r => r.map(v => '"' + String(v).replace(/"/g, '""') + '"').join(',')).join('\n');
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url;
      a.download = 'properties_export_' + new Date().toISOString().split('T')[0] + '.csv';
      a.click(); URL.revokeObjectURL(url);
    } catch { toast.error('Could not export properties'); }
  };

  if (loading) return <div className="loading-screen"><div className="spinner" /></div>;

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: '#f8f9fa' }}>
      <EmployeeSidebar />
      <div style={{ flex: 1, padding: '2rem' }}>
        <div style={{ fontSize: '1.8rem', fontWeight: 700, marginBottom: 4 }}>Landlords</div>
        <div style={{ fontSize: 14, color: '#64748b', marginBottom: 20 }}>All landlord accounts across the platform.</div>

        <input style={{ width: 320, padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 14, marginBottom: 16 }}
          placeholder="Search by name, company, or email..."
          value={search} onChange={e => setSearch(e.target.value)} />

        <button onClick={() => setShowAddProperty(true)} style={{ marginLeft: 12, padding: '8px 16px', background: '#7c3aed', color: '#fff', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
          + Add Property
        </button>

        {showAddProperty && (
          <AddPropertyModal
            onClose={() => setShowAddProperty(false)}
            onSuccess={() => { reloadLandlords(); setShowAddProperty(false); }}
          />
        )}

        <button onClick={() => setShowAddLandlord(true)} style={{ marginLeft: 12, padding: '8px 16px', background: '#16a34a', color: '#fff', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
          + Add Landlord
        </button>

        {showAddLandlord && (
          <AddLandlordModal
            onClose={() => setShowAddLandlord(false)}
            onSuccess={() => { reloadLandlords(); }}
          />
        )}

        <button onClick={() => setShowImport(true)} style={{ marginLeft: 12, padding: '8px 16px', background: '#1d4ed8', color: '#fff', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
          📥 Import CSV
        </button>

        {showImport && (
          <ImportTenantsModal
            onClose={() => setShowImport(false)}
            onSuccess={() => {}}
          />
        )}

        <div style={{ background: 'white', borderRadius: 12, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
          <div style={{ padding: '16px 20px', borderBottom: '1px solid #e2e8f0', fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span>All landlords ({filtered.length})</span>
            <button onClick={exportPropertiesCSV} style={{ background: '#2563eb', color: 'white', border: 'none', borderRadius: 8, padding: '7px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer', marginRight: 8 }}>⬇ Export Properties</button>
            <button onClick={exportCSV} style={{ background: '#16a34a', color: 'white', border: 'none', borderRadius: 8, padding: '7px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>⬇ Export Landlords</button>
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: '#f8fafc' }}>
                  {['Landlord', 'Company', 'Properties', 'Tenants', 'Account Manager', 'Status', 'Since', ''].map(h => (
                    <th key={h} style={{ padding: '10px 16px', textAlign: 'left', fontWeight: 600, color: '#64748b', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 && (
                  <tr><td colSpan={8} style={{ textAlign: 'center', color: '#94a3b8', padding: 24 }}>No landlords found</td></tr>
                )}
                {filtered.map(l => (
                  <>
                    <tr key={l.id} style={{ borderTop: '1px solid #f1f5f9' }}>
                      <td style={{ padding: '14px 16px' }}>
                        <div style={{ fontWeight: 600, color: '#2563eb', cursor: 'pointer', textDecoration: 'underline' }} onClick={() => navigate('/employee/landlords/' + l.id)}>{l.first_name} {l.last_name}</div>
                        <div style={{ fontSize: 11, color: '#94a3b8' }}>{l.email}</div>
                        {l.phone && <div style={{ fontSize: 11, color: '#94a3b8' }}>{l.phone}</div>}
                      </td>
                      <td style={{ padding: '14px 16px' }}>{l.company_name || '—'}</td>
                      <td style={{ padding: '14px 16px', fontWeight: 700 }}>{l.property_count}</td>
                      <td style={{ padding: '14px 16px', fontWeight: 700 }}>{l.tenant_count}</td>
                      <td style={{ padding: '14px 16px' }}>
                        {isSuperAdmin ? (
                          <select style={{ fontSize: 12, padding: '4px 8px', minWidth: 160, border: '1px solid #e2e8f0', borderRadius: 6 }}
                            value={l.account_manager_email ? accountManagers.find(am => am.email === l.account_manager_email)?.id || '' : ''}
                            onChange={e => assignManager(l.id, e.target.value)}
                            disabled={assigning[l.id]}>
                            <option value="">Unassigned</option>
                            {accountManagers.map(am => (
                              <option key={am.id} value={am.id}>{am.first_name} {am.last_name}</option>
                            ))}
                          </select>
                        ) : (
                          l.account_manager_name
                            ? <div><div style={{ fontSize: 13, fontWeight: 600 }}>{l.account_manager_name}</div><div style={{ fontSize: 11, color: '#94a3b8' }}>{l.account_manager_email}</div></div>
                            : <span style={{ color: '#94a3b8', fontSize: 12 }}>Unassigned</span>
                        )}
                      </td>
                      <td style={{ padding: '14px 16px' }}>
                        <span style={{ background: l.status === 'active' ? '#dcfce7' : '#fee2e2', color: l.status === 'active' ? '#16a34a' : '#dc2626', borderRadius: 100, padding: '3px 10px', fontSize: 11, fontWeight: 600 }}>
                          {l.status}
                        </span>
                      </td>
                      <td style={{ padding: '14px 16px', fontSize: 12 }}>{fmtDate(l.created_at)}</td>
                      <td style={{ padding: '14px 16px' }}>
                        <button onClick={() => toggleLandlord(l.id)}
                          style={{ background: 'none', border: '1px solid #e2e8f0', borderRadius: 6, padding: '4px 10px', cursor: 'pointer', fontSize: 12, color: '#64748b' }}>
                          {expanded[l.id] ? '▲ Hide' : '▼ Properties'}
                        </button>
                      </td>
                    </tr>
                    {expanded[l.id] && (
                      <tr key={l.id + '-exp'}>
                        <td colSpan={8} style={{ padding: '0 16px 16px', background: '#f8fafc' }}>
                          {!(properties[l.id] || []).length
                            ? <div style={{ padding: '12px 0', color: '#94a3b8', fontSize: 13 }}>No properties found.</div>
                            : <div style={{ display: 'flex', flexDirection: 'column', gap: 8, paddingTop: 12 }}>
                                {(properties[l.id] || []).map(p => (
                                  <div key={p.id}
                                    onClick={() => navigate(`/employee/properties/${p.id}`)}
                                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: 'white', border: '1px solid #e2e8f0', borderRadius: 10, padding: '12px 16px', cursor: 'pointer' }}>
                                    <div>
                                      <div style={{ fontWeight: 700, color: '#1e293b', fontSize: 14 }}>{p.name}</div>
                                      <div style={{ fontSize: 12, color: '#94a3b8' }}>{p.address}, {p.city}, {p.state}</div>
                                    </div>
                                    <div style={{ display: 'flex', gap: 20, alignItems: 'center' }}>
                                      <div style={{ textAlign: 'center' }}><div style={{ fontSize: 11, color: '#94a3b8' }}>Units</div><div style={{ fontWeight: 700 }}>{p.total_units}</div></div>
                                      <div style={{ textAlign: 'center' }}><div style={{ fontSize: 11, color: '#94a3b8' }}>Active</div><div style={{ fontWeight: 700, color: '#16a34a' }}>{p.active_tenants}</div></div>
                                      <div style={{ textAlign: 'center' }}><div style={{ fontSize: 11, color: '#94a3b8' }}>ISP</div><div style={{ fontWeight: 600, fontSize: 13 }}>{p.isp || '—'}</div></div>
                                      <div style={{ textAlign: 'center' }}><div style={{ fontSize: 11, color: '#94a3b8' }}>Min Speed</div><div style={{ fontWeight: 600, fontSize: 13 }}>{p.min_internet_speed || '—'}</div></div>
                                      <span style={{ background: '#eff6ff', color: '#2563eb', borderRadius: 6, padding: '5px 12px', fontSize: 12, fontWeight: 600 }}>View Contract →</span>
                                    </div>
                                  </div>
                                ))}
                              </div>
                          }
                        </td>
                      </tr>
                    )}
                  </>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        <LegalFooter />
      </div>
    </div>
  );
}
